/**
 * PRO-7 — In-app notifications.
 *
 * The `payload` on each row was audience-filtered AT OUTBOX-WRITE TIME, not at read time — a
 * producer-bound row physically cannot contain buyer identity or a selling price, even at rest
 * (CLAUDE.md; openapi `Notification`). So there is nothing to re-filter here.
 *
 * We still do not render the payload blindly. Each notification `type` gets an explicit i18n
 * template that names the exact payload keys it interpolates. That is deny-by-default applied to
 * the render layer: a future notification type that carried an unexpected field would render as
 * a generic line, not splatter the field onto the screen.
 *
 * Notably `product_priced` says "MOST set the catalog price — your product is visible to buyers"
 * and deliberately does NOT contain the price (openapi `setSellingPrice`: "fires `product_priced`
 * to the producer — a notification that deliberately does NOT contain the price").
 *
 * ─── THE TYPE KEYS ARE A CONTRACT ──────────────────────────────────────────────────────────
 * The `type` strings below MUST equal the backend `Type` constants in
 * backend/internal/notifications/domain.go, which states outright that those constants ARE the
 * i18n keys the frontends translate. A mismatch does not error — it silently falls through to
 * `generic`, and the producer sees a dateless-looking "Уведомление" with no content. That is
 * exactly what happened when this file matched `order_created` against the real
 * `order_created_producer`.
 *
 * ─── INLINE DETAIL ─────────────────────────────────────────────────────────────────────────
 * `GET /notifications` already returns the FULL filtered payload for every row (handlers.go
 * `InAppNotificationDTO`), so an order row can expand into this producer's own slice — items,
 * own base prices, slice total — with NO second request. The expansion renders from the same
 * payload that is already in the browser, and it can only show what
 * `ProducerOrderPayload` contains: no buyer identity, no selling price, no whole-order
 * aggregate. The absence is structural, exactly as on PRO-6.
 */
import * as React from 'react';
import { useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Bell, CheckCheck, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { Button, Card, EmptyState, ErrorState, TableSkeleton, cn, useToast } from '@/components/ui';
import { qk } from '@/shared/queryClient';
import * as api from '@/shared/api';
import { formatDateTime, formatInt, formatKg, formatUsd } from '@/shared/money';
import type { Notification } from '@/shared/contract';

export function NotificationsScreen() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const { toast } = useToast();

  const query = useInfiniteQuery({
    queryKey: qk.notifications,
    queryFn: ({ pageParam }) => api.listNotifications({ cursor: pageParam, limit: 24 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.markNotificationRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.notifications });
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });

  const markAll = useMutation({
    mutationFn: api.markAllNotificationsRead,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.notifications });
      void qc.invalidateQueries({ queryKey: qk.me });
      toast(t('common.saved'), 'success');
    },
  });

  const items = React.useMemo(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  );
  const unreadCount = query.data?.pages[0]?.unread_count ?? 0;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{t('notifications.title')}</h1>
        {unreadCount > 0 ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => markAll.mutate()}
            loading={markAll.isPending}
          >
            <CheckCheck className="h-3.5 w-3.5" aria-hidden />
            {t('notifications.markAllRead')}
          </Button>
        ) : null}
      </div>

      {query.isLoading ? (
        <TableSkeleton rows={5} cols={2} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={Bell}
          title={t('notifications.emptyTitle')}
          body={t('notifications.emptyBody')}
        />
      ) : (
        <>
          <Card className="divide-y divide-hairline">
            {items.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                locale={i18n.language}
                onRead={() => markRead.mutate(n.id)}
              />
            ))}
          </Card>

          {query.hasNextPage ? (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => void query.fetchNextPage()}
                loading={query.isFetchingNextPage}
              >
                {t('notifications.loadMore')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

// ── One row ─────────────────────────────────────────────────────────────────────────────────
function NotificationRow({
  notification,
  locale,
  onRead,
}: {
  notification: Notification;
  locale: string;
  onRead: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const unread = !notification.read_at;

  const { title, body } = renderNotification(notification, t);
  const slice = readOrderSlice(notification);
  const bodyId = `notification-detail-${notification.id}`;

  return (
    <div
      className={cn(
        'transition-colors',
        // Unread rows carry the admin's selection tint — the butter cream, not a foreign blue.
        unread ? 'bg-primary-muted/60' : 'bg-card',
      )}
    >
      <div className="flex items-start gap-4 px-5 py-4">
        {/* Unread emphasis carries a text label too, not just a colour (a11y §12). */}
        <div className="mt-1.5 w-2 shrink-0">
          {unread ? (
            <span
              className="block h-2 w-2 rounded-full bg-status-warning"
              aria-label={t('notifications.unread')}
            />
          ) : null}
        </div>

        <div className="min-w-0 flex-1">
          <p className={cn('text-sm', unread ? 'font-semibold text-foreground' : 'text-foreground/80')}>
            {title}
          </p>
          {body ? <p className="mt-0.5 text-sm text-muted-foreground">{body}</p> : null}
          <p className="mt-1 text-xs tabular-nums text-muted-foreground/70">
            {formatDateTime(notification.created_at, locale)}
          </p>

          {/*
            Detail affordances appear ONLY for an order notification, because it is the only
            payload with a slice to show. Everything else is a one-line statement that has no
            "more" behind it — offering an expander there would promise content that does not
            exist.
          */}
          {slice ? (
            <div className="mt-2 flex flex-wrap items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-controls={bodyId}
              >
                {open ? (
                  <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                )}
                {open ? t('notifications.collapse') : t('notifications.expand')}
              </Button>

              {/* Button is a real <button> here (no asChild), so navigation uses a Link. */}
              {slice.orderNumber ? (
                <Link
                  to={`/orders/${encodeURIComponent(slice.orderNumber)}`}
                  className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-link hover:underline"
                >
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                  {t('notifications.openOrder')}
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>

        {unread ? (
          <Button variant="ghost" size="sm" onClick={onRead}>
            {t('notifications.markRead')}
          </Button>
        ) : null}
      </div>

      {slice && open ? <OrderSliceDetail id={bodyId} slice={slice} /> : null}
    </div>
  );
}

/**
 * The inline slice — rendered from the payload already in memory, never a second request.
 *
 * Mirrors PRO-6 (`OrderSlice.tsx`) in both columns and wording, deliberately reusing the
 * `orders.*` i18n keys: the producer should recognise the same table, not learn a second
 * vocabulary for the same numbers. Like that screen, it shows the producer's OWN base prices
 * and the SLICE totals only — there is no order grand total and no truck fill here, because
 * those are computed across every producer's items.
 */
function OrderSliceDetail({ id, slice }: { id: string; slice: OrderSliceView }) {
  const { t } = useTranslation();

  return (
    <div id={id} className="border-t border-hairline bg-muted/30 px-5 py-4">
      {slice.buyerCountry ? (
        <p className="mb-3 text-xs text-muted-foreground">
          {/* The ONLY buyer attribute a producer ever receives. */}
          {t('orders.buyerCountry')}:{' '}
          <span className="font-medium text-foreground">{slice.buyerCountry}</span>
        </p>
      ) : null}

      {slice.items.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[34rem] text-sm">
            <caption className="sr-only">{t('orders.yourItems')}</caption>
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th scope="col" className="py-2 pr-4 font-medium">
                  {t('orders.product')}
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  {t('orders.qty')}
                </th>
                <th scope="col" className="py-2 pr-4 text-right font-medium">
                  {t('orders.pieces')}
                </th>
                <th scope="col" className="py-2 pr-4 text-right font-medium">
                  {t('orders.pricePerPiece')}
                </th>
                <th scope="col" className="py-2 text-right font-medium">
                  {t('orders.lineTotal')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {slice.items.map((line, i) => (
                <tr key={`${line.name}-${i}`}>
                  <td className="py-2 pr-4 font-medium text-foreground">{line.name}</td>
                  <td className="py-2 pr-4 text-muted-foreground">
                    {line.qtyInput} {line.qtyUnit ? t(`units.${line.qtyUnit}`) : ''}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums text-muted-foreground">
                    {formatInt(line.qtyPieces)}
                  </td>
                  {/* The producer's OWN base price snapshot. Never a selling price. */}
                  <td className="py-2 pr-4 text-right tabular-nums text-muted-foreground">
                    {formatUsd(line.pricePerPiece)}
                  </td>
                  <td className="py-2 text-right font-medium tabular-nums text-foreground">
                    {formatUsd(line.lineTotal)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap justify-end gap-x-6 gap-y-1 text-sm">
        {slice.sliceWeightKg ? (
          <span className="text-muted-foreground">
            {t('orders.sliceWeight')}:{' '}
            <span className="tabular-nums text-foreground/80">{formatKg(slice.sliceWeightKg)}</span>
          </span>
        ) : null}
        {slice.sliceTotalUsd ? (
          <span className="font-medium text-foreground/80">
            {t('orders.totalAtYourPrices')}:{' '}
            <span className="text-base font-semibold tabular-nums text-foreground">
              {formatUsd(slice.sliceTotalUsd)}
            </span>
          </span>
        ) : null}
      </div>

      <p className="mt-3 text-xs text-muted-foreground">{t('orders.anonymityNote')}</p>
    </div>
  );
}

// ── Payload reading ─────────────────────────────────────────────────────────────────────────

interface OrderSliceLineView {
  name: string;
  qtyInput: string;
  qtyUnit: string;
  qtyPieces: number;
  pricePerPiece: string;
  lineTotal: string;
}

interface OrderSliceView {
  orderNumber: string;
  buyerCountry: string;
  items: OrderSliceLineView[];
  sliceTotalUsd: string;
  sliceWeightKg: string;
}

/** Coerce a payload value to a display string. Anything unexpected becomes ''. */
function str(v: unknown): string {
  return typeof v === 'string' || typeof v === 'number' ? String(v) : '';
}

/**
 * Project an order notification's payload onto the view model — an ALLOWLIST, not a spread.
 *
 * Every field is named here explicitly, mirroring `ProducerOrderPayload`
 * (backend/internal/notifications/payloads.go). This is the same deny-by-default discipline the
 * one-line templates use: a field the backend adds later cannot reach the screen until someone
 * adds it here on purpose. Returns null for any type that has no slice to show.
 */
function readOrderSlice(n: Notification): OrderSliceView | null {
  if (n.type !== 'order_created_producer') return null;

  const p = n.payload ?? {};
  const rawItems = Array.isArray(p.items) ? p.items : [];

  const items = rawItems.map((raw): OrderSliceLineView => {
    const line = (raw ?? {}) as Record<string, unknown>;
    const pieces = Number(line.qty_pieces);
    return {
      name: str(line.product_name),
      qtyInput: str(line.qty_input),
      qtyUnit: str(line.qty_unit),
      qtyPieces: Number.isFinite(pieces) ? pieces : 0,
      pricePerPiece: str(line.price_per_piece),
      lineTotal: str(line.line_total_usd),
    };
  });

  return {
    orderNumber: str(p.order_number),
    buyerCountry: str(p.buyer_country),
    items,
    sliceTotalUsd: str(p.slice_total_usd),
    sliceWeightKg: str(p.slice_weight_kg),
  };
}

/**
 * Map a notification onto an explicit, translated template.
 *
 * The payload is trusted (filtered at write time) but not SPLATTED: each type names the keys it
 * reads. An unknown type degrades to a generic line rather than dumping raw JSON at the producer
 * — which, besides being ugly, is precisely how a future backend field would first become
 * visible on a producer's screen.
 *
 * The case labels are the backend `Type` constants VERBATIM. See the file header.
 */
function renderNotification(
  n: Notification,
  t: (key: string, opts?: Record<string, unknown>) => string,
): { title: string; body?: string } {
  const p = n.payload ?? {};
  const key = (k: string): string => str(p[k]);

  switch (n.type) {
    case 'order_created_producer':
      return {
        title: t('notifications.type.order_created_producer'),
        body: t('notifications.type.order_created_producer_body', {
          order_number: key('order_number'),
        }),
      };

    // Producers are NOT notified of status changes today — admin/service.go enqueues this type
    // to the buyer only, and PRO-6 documents that producers see status only when they look. The
    // case is kept so that if that policy ever changes the row reads correctly on day one,
    // rather than silently degrading to `generic` the way order_created_producer did.
    case 'order_status_changed': {
      const status = key('status');
      return {
        title: t('notifications.type.order_status_changed'),
        body: t('notifications.type.order_status_changed_body', {
          order_number: key('order_number'),
          status: status ? t(`status.order.${status}`) : '',
        }),
      };
    }

    case 'product_priced':
      // NO PRICE. By contract, this payload does not carry one, and this template does not ask.
      return {
        title: t('notifications.type.product_priced'),
        body: t('notifications.type.product_priced_body', { product_name: key('product_name') }),
      };

    case 'producer_approved':
      return {
        title: t('notifications.type.producer_approved'),
        body: t('notifications.type.producer_approved_body'),
      };

    case 'producer_rejected':
      return {
        title: t('notifications.type.producer_rejected'),
        body: t('notifications.type.producer_rejected_body'),
      };

    default:
      return { title: t('notifications.type.generic') };
  }
}
